/**
 * Spare-parts SAP endpoints. Mirrors al_spare_parts/src/utils/API.js but typed,
 * with the OData `d.results` envelope unwrapped at the boundary so callers
 * receive plain arrays.
 *
 * The SAP order endpoint returns `{ RefNo, paymentURL }` — both consumed by
 * the checkout flow (the iframe URL is what the customer pays through).
 */

import { apiGet, apiPost } from './client'
import { resolveApiBase } from './config'
import type { ODataResults } from './types'

// ---------- Types ----------

export type SpareProdFilter = 'SP' | 'PS' | string
export type SpareProdFam = 'WS' | 'WA' | 'DS' | 'SCD' | '' | string

export type SpareItem = {
  ProdFilter: SpareProdFilter
  ProdFam: SpareProdFam
  MaterialNo: string
  RefMaterial?: string
  Desc: string
  Info?: string
  Colour: string
  FullImage: string
  IsCharactImage: boolean
  Price: string
  GrossWeight: string
  NetWeight: string
  UOM: string
  SalesOrg: string
}

export type SpareProdFamRow = {
  Id: SpareProdFam
  Desc: string
  FullImage: string
  IsCharactImage: boolean
}

export type SpareProdFilterRow = {
  Key: SpareProdFilter
  Text: string
}

export type SpareColour = {
  Desc: string
  ColourCode: string
  Sortorder: number
}

export type CollectOption = {
  OptNo: string
  Description: string
  Street: string
  Suburb: string
  State: string
  Postcode: string
}

export type ShippingOption = {
  OptNo: string
  Description: string
  Length: string
  Width: string
  Height: string
  Weight: string
  UomLength: string
  UomWidth: string
  UomHeight: string
  UomWeight: string
  Price: string
  Currency: string
}

export type SpareOrderLineItem = {
  MaterialNo: string
  Qty: number
}

export type SpareOrderPayload = {
  Name: string
  Email: string
  TelNumber: string
  Street: string
  PostCode: string
  City: string
  State: string
  /** "Pickup" or "Delivery" — sent verbatim. */
  ShipmentType: 'Pickup' | 'Delivery'
  /**
   * For Pickup: the collect-option OptNo (e.g. "Z001").
   * For Delivery: the shipping option Description (e.g. "Satchel (P5)").
   * The inconsistency is preserved from the old code to keep the SAP contract identical.
   */
  ShipmentValue: string
  ToItm: SpareOrderLineItem[]
}

export type SpareOrderResponse = {
  RefNo: string
  paymentURL: string
}

// ---------- Endpoints ----------

export async function getSpareItems(): Promise<SpareItem[]> {
  // SAP returns the OData `d.results` envelope here — same shape as the other
  // SAP-backed endpoints. The legacy axios consumer in API.js never unwrapped
  // it explicitly; the legacy Landing page handled the wrapped shape directly.
  const res = await apiGet<ODataResults<SpareItem>>('/v1/sp/items')
  return res.d.results
}

export async function getSpareProdFam(): Promise<SpareProdFamRow[]> {
  const res = await apiGet<ODataResults<SpareProdFamRow>>('/v1/sp/prodFam')
  return res.d.results
}

export async function getSpareProdFilter(): Promise<SpareProdFilterRow[]> {
  const res = await apiGet<ODataResults<SpareProdFilterRow>>('/v1/sp/prodFilter')
  return res.d.results
}

export async function getSpareColours(): Promise<SpareColour[]> {
  const res = await apiGet<ODataResults<SpareColour>>('/v1/sp/colours')
  return res.d.results
}

export async function getCollectOptions(): Promise<CollectOption[]> {
  const res = await apiGet<ODataResults<CollectOption>>('/v1/sp/collectOptions')
  return res.d.results
}

export async function getShippingOptions(): Promise<ShippingOption[]> {
  const res = await apiGet<ODataResults<ShippingOption>>('/v1/sp/shippingOptions')
  return res.d.results
}

export async function postSpareOrder(payload: SpareOrderPayload): Promise<SpareOrderResponse> {
  return apiPost<SpareOrderResponse>('/v1/sp/order', payload)
}

export function getSpareImageSrc(fileName: string | undefined, isCharImage: boolean): string | undefined {
  if (!fileName) return undefined
  const base = resolveApiBase().replace(/\/$/, '')
  return `${base}/v1/sp/image?fileName=${encodeURIComponent(fileName)}&isCharImage=${isCharImage}`
}

// ---------- Email mailers (transactional, no SAP) ----------

export type SpareEmailHelp = { name: string; email: string; phone: string; state: string }
export type SpareEmailWarranty = { name: string; email: string; phone: string }

export async function postSpareEmailHelp(body: SpareEmailHelp): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/v1/sp/email/help', body)
}

export async function postSpareEmailWarranty(body: SpareEmailWarranty): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/v1/sp/email/warranty', body)
}
